import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  TriggerSectionTitle,
  TriggerSectionSubtitle,
} from '../AssistantScriptDialog.styled';
import {
  PlaybackSection,
  PolicyDropdown,
  PolicyDropdownHeader,
  PolicyDropdownItem,
  PolicyBadge,
  PolicyHeaderRow,
  PolicyNameRow,
  PolicyIcon,
  PolicyName,
  PolicyHeaderDesc,
  PolicyItemDesc,
  PolicyItemContent,
  PolicyRadioOuter,
  PolicyRadioInner,
  PolicyExpandIcon,
} from './styled';
import { PlaybackPolicySectionProps } from './interfaces';
import { POLICIES } from './constants';

const PlaybackPolicySection = ({
  playbackPolicy,
  onPlaybackPolicyChange,
  skipFirstSession,
  onSkipFirstSessionChange,
}: PlaybackPolicySectionProps) => {
  void skipFirstSession;
  void onSkipFirstSessionChange;

  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const selected = POLICIES.find((p) => p.id === playbackPolicy) ?? POLICIES[1];

  return (
    <PlaybackSection>
      <TriggerSectionTitle>{t('assistant.scriptDialog.playbackPolicy')}</TriggerSectionTitle>
      <TriggerSectionSubtitle>
        {t('assistant.scriptDialog.playbackPolicySubtitle')}
      </TriggerSectionSubtitle>

      <PolicyDropdown $color={selected.color}>
        <PolicyDropdownHeader
          $bg={selected.bg}
          $color={selected.color}
          $open={open}
          onClick={() => setOpen(!open)}
        >
          <PolicyHeaderRow>
            <PolicyIcon>{selected.icon}</PolicyIcon>
            <Box>
              <PolicyNameRow>
                <PolicyName $color={selected.color}>
                  {t(`assistant.scriptDialog.policies.${selected.id}.name`)}
                </PolicyName>
                {selected.badge && (
                  <PolicyBadge
                    $color={selected.color}
                    $bg={selected.bg}
                    $hasBorder
                  >
                    {t(`assistant.scriptDialog.policies.${selected.id}.badge`)}
                  </PolicyBadge>
                )}
              </PolicyNameRow>
              <PolicyHeaderDesc $color={selected.color}>
                {t(`assistant.scriptDialog.policies.${selected.id}.desc`)}
              </PolicyHeaderDesc>
            </Box>
          </PolicyHeaderRow>
          <PolicyExpandIcon $open={open} />
        </PolicyDropdownHeader>

        {open &&
          POLICIES.map((p, i) => {
            const isSelected = playbackPolicy === p.id;
            return (
              <PolicyDropdownItem
                key={p.id}
                $isSelected={isSelected}
                $bg={p.bg}
                $isLast={i === POLICIES.length - 1}
                onClick={() => {
                  onPlaybackPolicyChange(p.id);
                  setOpen(false);
                }}
              >
                <PolicyIcon>{p.icon}</PolicyIcon>
                <PolicyItemContent>
                  <PolicyNameRow>
                    <PolicyName $color={p.color} $isSelected={isSelected}>
                      {t(`assistant.scriptDialog.policies.${p.id}.name`)}
                    </PolicyName>
                    {p.badge && (
                      <PolicyBadge
                        $color={isSelected ? p.color : '#1d4ed8'}
                        $bg={isSelected ? p.bg : '#dbeafe'}
                      >
                        {t(`assistant.scriptDialog.policies.${p.id}.badge`)}
                      </PolicyBadge>
                    )}
                  </PolicyNameRow>
                  <PolicyItemDesc $color={p.color} $isSelected={isSelected}>
                    {t(`assistant.scriptDialog.policies.${p.id}.desc`)}
                  </PolicyItemDesc>
                </PolicyItemContent>
                <PolicyRadioOuter $color={p.color} $isSelected={isSelected}>
                  {isSelected && <PolicyRadioInner $color={p.color} />}
                </PolicyRadioOuter>
              </PolicyDropdownItem>
            );
          })}
      </PolicyDropdown>
    </PlaybackSection>
  );
};

export default PlaybackPolicySection;
